/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/** Same rules as the registration email (PRIC-180). */
const EMAIL_MAX_LENGTH = 100;
const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const PARTNER_ID_REGEX = /^\d+$/;

export type IdentifierError = 'empty' | 'invalidEmail' | 'invalidPartnerId';

/** The identifier is an email when it contains "@", otherwise a partner ID. */
export function isEmailIdentifier(identifier: string): boolean {
    return identifier.includes('@');
}

/**
 * Validates the "ID soci@ o Correo electrónico" value. Shared by the form (client) and the
 * login action (server) so both reject the same input.
 * @returns The error, or null when the identifier can be sent to Auth Extension
 */
export function validateIdentifier(rawIdentifier: string): IdentifierError | null {
    const identifier = rawIdentifier.trim();

    if (!identifier) {
        return 'empty';
    }

    if (isEmailIdentifier(identifier)) {
        return identifier.length <= EMAIL_MAX_LENGTH && EMAIL_REGEX.test(identifier) ? null : 'invalidEmail';
    }

    return PARTNER_ID_REGEX.test(identifier) ? null : 'invalidPartnerId';
}
